import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Modal } from '~/components/modal/modal-old';
import useTranslations from '~/components/translation/use-translations';
import Loading from '~/components/misc/loading';
import { WE_SPIN_MODALS_TEXT_KEYS } from '../../constants/we-spin-text-keys';
import { selectDisplayName } from '../../ducks/we-spin-selectors';
import { Input } from '@comeon/ui';
import BinaryQuestion from '~/components/binary-question';
import { updateDisplayName } from '../../ducks/we-spin-duck';
import styled, { css } from 'styled-components';
import theme from 'styled-theming';

const BinaryQuestionWrapper = styled(BinaryQuestion)`
	p {
		color: rgba(var(--font-supportive), 1);

		${theme('brand', {
			comeon: css`
				color: rgba(var(--font-light), 1) !important;
			`,
			suomikasino: css`
				color: rgba(var(--font-dark), 1) !important;
			`,
			getlucky: css`
				color: rgba(var(--font-dark), 1) !important;
			`,
			mobilebet: css`
				color: rgba(var(--font-light), 1) !important;
			`,
			nopeampi: css`
				color: rgba(var(--font-dark), 1) !important;
			`,
			galaksino: css`
				color: rgba(var(--font-light), 1) !important;
			`
		})}
	}
`;

const validationReg = new RegExp(/^[a-zA-Z][a-zA-Z0-9_.-]{1,20}$/);
const inputPattern = '/^[a-zA-Z][a-zA-Z0-9_.-]{1,20}$/';

/**
 * Modal for updating the username.
 *
 * @param {Object} props - Component props.
 * @param {boolean} props.openUpdateUsername - Whether the update username modal is open.
 * @param {Function} props.closeUpdateUsername - Function to close the update username modal.
 * @param {Function} [props.onSuccess] - Optional callback function to be called on success.
 * @returns {JSX.Element}
 */
const UpdateUsernameModal = props => {
	const dispatch = useDispatch();
	const displayName = useSelector(selectDisplayName) || '';
	const { translations: tr, isLoadingTranslations } = useTranslations(WE_SPIN_MODALS_TEXT_KEYS);

	const [submitting, setSubmitting] = useState(false);
	const [usernameValue, setUsernameValue] = useState(displayName);
	const [valid, setValid] = useState();

	const updateUsername = useCallback(async () => {
		try {
			setSubmitting(true);
			await dispatch(updateDisplayName(usernameValue));
			props.closeUpdateUsername();
			if (props.onSuccess) {
				props.onSuccess();
			}
		} finally {
			setSubmitting(false);
		}
	}, [dispatch, props, usernameValue]);

	const onChangeHandler = useCallback(e => {
		setUsernameValue(e.target.value);
		setValid(validationReg.test(e.target.value));
		// e.currentTarget.checkValidity()
	}, []);

	if (isLoadingTranslations) return <Loading />;

	return (
		<Modal show={props.openUpdateUsername} position="fixed">
			<BinaryQuestionWrapper
				shouldOpenBinary
				openInSidebar
				buttonsHasUncontrolledLoading={submitting}
				title={tr.WE_SPIN_BUY_IN_ENTER_USERNAME_TITLE}
				text={tr.WE_SPIN_BUY_IN_CREATE_USERNAME_MSG}
				yesText={tr.WE_SPIN_BUY_IN_CREATE_USERNAME_CONFIRM}
				noText={tr.WE_SPIN_BUY_IN_CREATE_USERNAME_CANCEL}
				placeholder={tr.WE_SPIN_BUY_IN_ENTER_USERNAME_PLACEHOLDER}
				inputLabel=""
				// inputValue={0} /* using min and inputValue to force binary question valid state */
				// min={valid ? 0 : 1} /* binary question should be refactored */
				onYesClick={updateUsername}
				onNoClick={props.closeUpdateUsername}
			>
				<Input
					autoFocus
					forceFocus
					required
					type="text"
					inputRef="binaryInput"
					label=""
					pattern={inputPattern}
					validationMessage={
						valid !== undefined ? (valid ? null : tr.WE_SPIN_CHANGE_USERNAME_NAME_VALIDATION_ERROR) : null
					}
					placeholder={tr.WE_SPIN_BUY_IN_ENTER_USERNAME_PLACEHOLDER}
					onChange={onChangeHandler}
					value={usernameValue}
				/>
			</BinaryQuestionWrapper>
		</Modal>
	);
};

export default UpdateUsernameModal;
